import { useLayoutEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

interface ShrinkToFitProps {
  text: string
  maxFontSize?: number
  minFontSize?: number
  className?: string
}

// Render `text` at `maxFontSize` and shrink to fit the container's width.
// Measure-based so it stays on one line at any breakpoint or container size.
const ShrinkToFit = ({ text, maxFontSize = 56, minFontSize = 20, className }: ShrinkToFitProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLSpanElement>(null)
  const [fontSize, setFontSize] = useState(maxFontSize)

  useLayoutEffect(() => {
    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return

    const recompute = () => {
      const containerWidth = container.clientWidth
      const textWidth = measure.scrollWidth
      if (textWidth === 0 || containerWidth === 0) return
      const ratio = containerWidth / textWidth
      // 0.99 guards against subpixel rounding pushing the fitted size a hair
      // over container width (scrollWidth is integer-rounded).
      setFontSize(Math.max(minFontSize, Math.min(maxFontSize, maxFontSize * ratio * 0.99)))
    }

    recompute()
    const ro = new ResizeObserver(recompute)
    ro.observe(container)
    // Webfonts can finish loading after first paint; recompute against the real
    // Figtree metrics instead of the fallback font.
    void document.fonts?.ready.then(recompute)
    return () => ro.disconnect()
  }, [text, maxFontSize, minFontSize])

  return (
    <div ref={containerRef} className="w-full">
      {/* Hidden measurer at max size — drives the shrink ratio. */}
      <span
        ref={measureRef}
        aria-hidden
        className={cn('invisible absolute whitespace-nowrap', className)}
        style={{ fontSize: maxFontSize, lineHeight: 1 }}
      >
        {text}
      </span>
      <div className={cn('whitespace-nowrap', className)} style={{ fontSize }}>
        {text}
      </div>
    </div>
  )
}

export default ShrinkToFit
